import { CONSTANTS, type Columns, type TableToolbarFilterConfig } from '@bitstopco/bitstop-theme';

import { Box, Tooltip, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { Link } from 'react-router-dom';

import ListingMediaCell from '@/components/marketing/media/ListingMediaCell';
import Chip from '@/components/ui/Chip';
import MorePopover from '@/components/ui/MorePopover';
import TableColumnClickableText from '@/components/ui/table/TableColumnClickableText';
import TableColumnDate from '@/components/ui/table/TableColumnDate';
import { US_STATE_OPTIONS } from '@/constants/app/usStates';

import type { ListingView, QueueItem } from '@/types';

export const BULK_SELECT_MAX_LIMIT = 100;

export const LISTINGS_CARDS = {
  published: {
    title: 'Published',
  },
  not_published: {
    title: 'Not Published',
  },
  in_review: {
    title: 'Awaiting Publishing',
  },
};

export const QUEUE_CARDS = {
  create_queue: {
    title: 'Pending Creation',
  },
  republish_queue: {
    title: 'Pending Update',
  },
  unpublish_queue: {
    title: 'Pending Unpublish',
  },
  queue_errors: {
    title: 'Error Publishing',
  },
};

export const STATUS_LISTING_CHIP: Record<string, { label: string; color: ChipProps['color'] }> = {
  published: { label: 'Published', color: 'success' },
  unpublished: { label: 'Not Published', color: 'error' },
  suggestion_pending: { label: 'Awaiting Unpublish', color: 'warning' },
  in_review: { label: 'Awaiting Publishing', color: 'warning' },
};

/**
 * Listings columns configuration for the Marketing / Listings tab.
 */
export const LISTINGS_COLUMNS = (onAction: (item: ListingView) => void): Columns<ListingView> => ({
  defaultValue: '-',
  labelSx: { whiteSpace: 'nowrap' },
  valueSx: { whiteSpace: 'nowrap' },
  items: [
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (value: ListingView['location_id'], data) => (
        <TableColumnClickableText
          id={String(value)}
          text={data.name ?? `Location ${value}`}
          clipboardValue={String(value)}
          name="Location ID"
          fixIcon
        />
      ),
      valueSx: {
        minWidth: 250,
        '& h6': { maxWidth: 400, whiteSpace: 'normal', wordWrap: 'break-word' },
      },
    },
    {
      value: 'image_urls',
      label: 'Media',
      hideSort: true,
      valueMapper: (_value: ListingView['image_urls'], data) => <ListingMediaCell listing={data} />,
    },
    {
      value: 'full_address',
      label: 'Address',
      valueMapper: (value: ListingView['full_address']) =>
        value ? (
          <Tooltip title={value}>
            <Typography noWrap variant="body2" sx={{ maxWidth: 220 }}>
              {value}
            </Typography>
          </Tooltip>
        ) : (
          <Typography variant="body2">-</Typography>
        ),
    },
    {
      value: 'atm_ids',
      label: 'Machine',
      valueMapper: (value: ListingView['atm_ids']) => {
        if (!value || value.length === 0) return <Chip label="No machine installed" />;

        if (value.length === 1) {
          return (
            <TableColumnClickableText
              id={String(value[0])}
              to={`/machine/${value[0]}`}
              text={`Machine ${value[0]}`}
              fixIcon
              clipboardValue={String(value[0])}
              name="Machine ID"
              overrideTooltipLinkText="Go to Machine"
            />
          );
        }

        return (
          <>
            {value.map((id) => (
              <Link to={`/machine/${id}`} key={id}>
                <Chip
                  label={`Machine ${id}`}
                  sx={{ marginRight: 1, marginBottom: 1, zIndex: 100 }}
                />
              </Link>
            ))}
          </>
        );
      },
    },
    {
      value: 'operator_id',
      label: 'Operator',
      valueMapper: (value: ListingView['operator_id'], data) =>
        value ? (
          <TableColumnClickableText
            id={String(value)}
            text={data.operator_name ?? String(value)}
            clipboardValue={String(value)}
            name="Operator ID"
          />
        ) : (
          <Typography variant="body2">-</Typography>
        ),
    },
    {
      value: 'integration_name',
      label: 'Integrator',
      valueMapper: (value: ListingView['integration_name']) => (
        <Typography variant="body2">{value ?? '-'}</Typography>
      ),
    },
    {
      value: 'gmb_group_id',
      label: 'GMB Group',
      valueMapper: (_value: ListingView['gmb_group_id'], data) =>
        data.gmb_group_name ? (
          <Box sx={{ display: 'flex' }}>
            <Chip
              color="info"
              label={data.gmb_group_name}
              sx={{ fontWeight: 600, fontSize: '12px' }}
            />
          </Box>
        ) : (
          <Typography variant="body2">-</Typography>
        ),
    },
    {
      value: 'marketing_status',
      label: 'Status',
      valueMapper: (value: ListingView['marketing_status']) => {
        const chip = STATUS_LISTING_CHIP[value] ?? {
          label: value || 'No Status',
          color: 'default' as ChipProps['color'],
        };
        return (
          <Box sx={{ display: 'flex' }}>
            <Chip
              color={chip.color}
              label={chip.label}
              sx={{ fontWeight: 'bold', fontSize: '12px' }}
            />
          </Box>
        );
      },
    },
    {
      value: 'last_updated_at',
      label: 'Actions',
      hideSort: true,
      valueMapper: (_value, item) => (
        <MorePopover items={['View Details']} onClick={() => onAction(item)} />
      ),
    },
  ],
});

/**
 * Listings filters configuration for the Marketing / Listings tab.
 */
export const LISTINGS_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    type: 'select',
    searchable: false,
    multiple: true,
    operator: 'equal',
    property: 'marketing_status',
    showFooter: true,
    placeholder: 'Status',
    clearText: 'Clear filter',
    label: 'Status',
    options: [
      { label: 'Published', value: 'published' },
      { label: 'Not Published', value: 'unpublished' },
      { label: 'Awaiting Publishing', value: 'in_review' },
    ],
  },
  {
    type: 'select',
    searchable: true,
    multiple: false,
    operator: 'equal',
    property: 'state',
    showFooter: true,
    placeholder: 'State',
    clearText: 'Clear filter',
    label: 'State',
    options: US_STATE_OPTIONS,
  },
  {
    property: 'operator_id',
    type: 'select-ids',
    placeholder: 'Operator',
    subtitleText: 'Add operator ID to filter',
    inputPlaceholder: 'Enter operator ID',
    label: 'Operator',
    operator: 'equal',
    validate: async () => null,
    mapperSelectedValue: (value: string) => `Operator ${value}`,
    mapperSelectedValues: (count: number) => `Operator (${count})`,
  },
  {
    property: 'atm_ids',
    type: 'select-ids',
    placeholder: 'Machine',
    subtitleText: 'Add machine ID to filter',
    inputPlaceholder: 'Enter machine ID',
    label: 'Machine ID',
    operator: 'equal',
    validate: async () => null,
    mapperSelectedValue: (value: string) => `Machine ${value}`,
    mapperSelectedValues: (count: number) => `Machine ID (${count})`,
  },
  {
    property: 'location_id',
    type: 'select-ids',
    placeholder: 'Location',
    subtitleText: 'Add location ID to filter',
    inputPlaceholder: 'Enter location ID',
    label: 'Location ID',
    operator: 'equal',
    validate: async () => null,
    mapperSelectedValue: (value: string) => `Location ${value}`,
    mapperSelectedValues: (count: number) => `Location ID (${count})`,
  },
  {
    property: 'gmb_group_name',
    type: 'select-ids',
    placeholder: 'GMB Group',
    subtitleText: 'Add GMB group name to filter',
    inputPlaceholder: 'Enter GMB group name',
    label: 'GMB Group',
    operator: 'equal',
    validate: async () => null,
    mapperSelectedValue: (value: string) => value,
    mapperSelectedValues: (count: number) => `GMB Group (${count})`,
  },
  {
    type: 'select',
    searchable: false,
    multiple: false,
    operator: 'equal',
    property: 'has_media',
    showFooter: true,
    placeholder: 'Media',
    clearText: 'Clear filter',
    label: 'Media',
    options: [
      { label: 'With media', value: 'true' },
      { label: 'Without media', value: 'false' },
    ],
  },
];

export const ACTION_CHIP: Record<
  QueueItem['action'],
  { label: string; color: ChipProps['color'] }
> = {
  create: { label: 'Create', color: 'default' },
  update: { label: 'Update', color: 'default' },
  unpublish: { label: 'Unpublish', color: 'default' },
  republish: { label: 'Republish', color: 'default' },
};

export const STATUS_CHIP: Record<
  QueueItem['status'],
  { label: string; color: ChipProps['color'] }
> = {
  pending: { label: 'Pending', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'error' },
  error: { label: 'Error', color: 'error' },
  obsolete: { label: 'Obsolete', color: 'default' },
  completed: { label: 'Completed', color: 'success' },
  conflict: { label: 'Conflict', color: 'warning' },
};

/**
 * Queue columns configuration.
 * Accepts callbacks for per-row publish/reject actions and a permission flag.
 */
export const QUEUE_COLUMNS = (
  onApprove: (item: QueueItem) => void,
  onReject: (item: QueueItem) => void,
  canManage: boolean,
): Columns<QueueItem> => ({
  defaultValue: '-',
  labelSx: { whiteSpace: 'nowrap' },
  valueSx: { whiteSpace: 'nowrap' },
  items: [
    {
      value: 'location_id',
      label: 'Location',
      valueMapper: (value: QueueItem['location_id'], data) => (
        <TableColumnClickableText
          id={String(value)}
          text={data.location_name ?? `Location ${value}`}
          clipboardValue={String(value)}
          name="Location ID"
          to={`/location/${value}`}
          fixIcon
        />
      ),
      valueSx: {
        minWidth: 250,
        '& h6': { maxWidth: 400, whiteSpace: 'normal', wordWrap: 'break-word' },
      },
    },
    {
      value: 'action',
      label: 'Action',
      valueMapper: (value: QueueItem['action']) => {
        const chip = ACTION_CHIP[value] ?? { label: value, color: 'default' as ChipProps['color'] };
        return (
          <Chip
            color={chip.color}
            label={chip.label}
            sx={{ fontWeight: 'bold', fontSize: '12px' }}
          />
        );
      },
    },
    {
      value: 'status',
      label: 'Status',
      valueMapper: (value: QueueItem['status']) => {
        const chip = STATUS_CHIP[value] ?? { label: value, color: 'default' as ChipProps['color'] };
        return (
          <Chip
            color={chip.color}
            label={chip.label}
            sx={{ fontWeight: 'bold', fontSize: '12px' }}
          />
        );
      },
    },
    {
      value: 'change_occurred_at',
      label: 'Changed At',
      valueMapper: (value: QueueItem['change_occurred_at']) =>
        value ? <TableColumnDate value={value} /> : <>-</>,
    },
    {
      value: 'created_at',
      label: 'Submitted At',
      valueMapper: (value: QueueItem['created_at']) =>
        value ? <TableColumnDate value={value} /> : <>-</>,
    },
    {
      value: 'id',
      label: 'Actions',
      hideSort: true,
      valueMapper: (_value, item) => {
        if (!canManage) return null;
        const canReject = item.status === 'pending';
        const menuItems = canReject ? ['Approve Change', 'Reject Change'] : ['Approve Change'];
        return (
          <MorePopover
            items={menuItems}
            onClick={(index: number) => {
              if (index === 0) onApprove(item);
              if (index === 1 && canReject) onReject(item);
            }}
          />
        );
      },
    },
  ],
});

/**
 * Queue filters configuration.
 */
export const QUEUE_FILTERS_CONFIG: TableToolbarFilterConfig[] = [
  {
    property: 'created_at',
    operator: 'less_than_or_equal',
    type: 'select-timeframe',
    label: 'Select a time frame',
    placeholder: 'Queued At',
    showFooter: true,
    hideApplyButton: true,
    options: [...CONSTANTS.TIME_FRAME],
  },
  {
    type: 'select',
    searchable: false,
    multiple: true,
    operator: 'equal',
    property: 'status',
    showFooter: true,
    placeholder: 'Status',
    clearText: 'Clear filter',
    label: 'Status',
    options: [
      { label: 'Pending', value: 'pending' },
      { label: 'Approved', value: 'approved' },
      { label: 'Completed', value: 'completed' },
      { label: 'Rejected', value: 'rejected' },
      { label: 'Error', value: 'error' },
    ],
  },
  {
    type: 'select',
    searchable: false,
    multiple: true,
    operator: 'equal',
    property: 'action',
    showFooter: true,
    placeholder: 'Action',
    clearText: 'Clear filter',
    label: 'Action',
    options: [
      { label: 'Create', value: 'create' },
      { label: 'Update', value: 'update' },
      { label: 'Unpublish', value: 'unpublish' },
      { label: 'Republish', value: 'republish' },
    ],
  },
];
